import {
  Col,
  Container,
  Dropdown,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { ReactComponent as AGSearchIcon } from "../../assets/images/AGSearchIcon.svg";
import { ReactComponent as SearchXIcon } from "../../assets/images/SearchXIcon.svg";
import { ReactComponent as Download } from "../../assets/images/download-icon.svg";
import { ReactComponent as Share } from "../../assets/images/sharedocument.svg";
import { reportPlaceholder } from "../../constants/firm/labels";
import AgGrid from "../table/AgGrid";
import { useGetUsageReportDetailsQuery } from "../../slices/reportApiManagementSlice";
import Loader from "../Loader/Loader";
import { useDownloadUsageReportMutation } from "../../slices/reportApiManagementSlice";
import { toast } from "react-toastify";
import ShareReportModalPopup from "../modal/ShareReportModalPopup";

export default function UsageReport() {
  const [searchText, setSearchText] = useState("");
  const [rowData, setRowData] = useState([]);
  const [capacityUtil, setCapacityUtil] = useState(0);
  const [docPerUser, setDocPerUser] = useState(0);
  const [capacityPerUser, setCapacityPerUser] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [fileName, setFileName] = useState("");

  let empnameWidth = 250;
  let empnameMinWidth = 250;

  if (window.innerWidth < 576) {
    empnameWidth = 100;
    empnameMinWidth = 140;
  }

  const { data, isLoading } = useGetUsageReportDetailsQuery({
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data) {
      let count = 0;
      for (let i = 0; i < data?.length; i++) {
        count += data[i]?.capacityUsedPerFirm;
      }
      setCapacityUtil(count.toFixed(2));
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      let count = 0;
      let averageDoc = 0;
      for (let i = 0; i < data?.length; i++) {
        count = count + data[i]?.avgDocPerUser;
        averageDoc = count / data.length;
      }
      setDocPerUser(Math.ceil(averageDoc));
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      let count = 0;
      let averageCapacity = 0;
      for (let i = 0; i < data?.length; i++) {
        count = count + data[i]?.avgCapacityPerUser;
        averageCapacity = count / data.length;
      }
      let formattedCapacity = averageCapacity.toFixed(2);

      if (parseFloat(formattedCapacity) === 0) {
        formattedCapacity = (0).toFixed(2);
      }

      setCapacityPerUser(formattedCapacity);
    }
  }, [data]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };
  const handleClearSearch = () => {
    setSearchText("");
  };
  const GridFirmName = (params) => {
    const { value } = params;
    const firmName = value.length > 20 ? value.substring(0, 20) + "..." : value;

    return (
      <>
        {value.length > 20 ? (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${value}`}>{value}</Tooltip>}
          >
            <div className="truncate-tooltip grid-firm-email-anchor">
              {firmName}
            </div>
          </OverlayTrigger>
        ) : (
          <div className="truncate-tooltip grid-firm-email-anchor">
            {firmName}
          </div>
        )}
      </>
    );
  };

  const CenterAligned = (p) => {
    return <div className="grid-center-aligned-text">{`${p.value}`}</div>;
  };

  const getRowStyle = () => {
    return { borderBottom: "unset" };
  };

  const [columnDefs] = useState([
    {
      headerName: reportPlaceholder.firmName,
      field: "firmName",
      width: empnameWidth,
      minWidth: empnameMinWidth,
      sortable: true,
      unSortIcon: true,
      pinned: "left",
      cellRenderer: GridFirmName,
    },
    {
      headerName: reportPlaceholder.firmType,
      minWidth: 150,
      field: "firmType",
    },
    {
      headerName: reportPlaceholder.capacityUsed,
      minWidth: 150,
      field: "capacityUsedPerFirm",
      cellRenderer: CenterAligned,
      headerClass: "center-aligned-header",
    },
    {
      headerName: reportPlaceholder.activeUsers,
      minWidth: 150,
      field: "activeUser",
      cellRenderer: CenterAligned,
      headerClass: "center-aligned-header",
    },
    {
      headerName: reportPlaceholder.avgDocPerUser,
      minWidth: 200,
      field: "avgDocPerUser",
      cellRenderer: CenterAligned,
      headerClass: "center-aligned-header",
    },
    {
      headerName: reportPlaceholder.avgCapacityUtilPerUser,
      minWidth: 200,
      field: "avgCapacityPerUser",
      cellRenderer: CenterAligned,
      headerClass: "center-aligned-header",
    },
  ]);
  const headerRow = [
    {
      firmName: reportPlaceholder.firmName,
      firmType: reportPlaceholder.firmType,
      capacityUsedPerFirm: reportPlaceholder.capacityUsed,
      activeUser: reportPlaceholder.activeUsers,
      avgDocPerUser: reportPlaceholder.avgDocPerUser,
      avgCapacityPerUser: reportPlaceholder.avgCapacityUtilPerUser,
    }
  ]
  useEffect(() => {
    if (data) {
      setRowData(data);
    }
  }, [data]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      suppressMovable: false,
      sortable: false,
      wrapHeaderText: true,
      wrapText: true,
      headerClass: "custom-header",
      resizable: false,
      lockPosition: true,
      filter: false,
      minWidth: 150,
    };
  }, []);

  const paginationSizeLimit = 10;

  useEffect(() => {
    if (data) {
      const formattedData = data.map((item) => ({
        ...item,
        capacityUsedPerFirm: `${item.capacityUsedPerFirm || 0} GB`,
        avgCapacityPerUser: `${item.avgCapacityPerUser || 0} GB`,
      }));
      setRowData(formattedData);
    }
  }, [data]);

  const customFilter = useMemo(() => {
    if (!searchText) return rowData;

    const searchTextLower = searchText.toLowerCase();

    return rowData.filter((item) => {
      return (
        item.firmName?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.firmType?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.capacityUsedPerFirm.toLowerCase().includes(searchTextLower) ||
        item.activeUser
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        item.avgDocPerUser
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        item.avgCapacityPerUser.toLowerCase().includes(searchTextLower)
      );
    });
  }, [searchText, rowData]);

  const [triggerDownload, { isLoading: download }] =
    useDownloadUsageReportMutation();

  const handleShare = () => {
    setShowModal(true);
  };

  useEffect(() => {
    const fileName = `Usage`;
    setFileName(fileName);
  }, []);

  const filteredFields = customFilter.map((item) => ({
    firmName: item.firmName,
    firmType: item.firmType,
    capacityUsedPerFirm: item.capacityUsedPerFirm,
    activeUser: item.activeUser,
    avgDocPerUser: item.avgDocPerUser,
    avgCapacityPerUser: item.avgCapacityPerUser,
  }));
  const handleDownload = async (type) => {
    if (data) {
      let fileType = type.toLowerCase();
      let reportTitle = "Firm Data Usage Report";
      let fileName = `Firm_Usage_Report.${fileType}`;

      try {
        const result = await triggerDownload({
          reportTitle,
          data: filteredFields,
          type: fileType,
        }).unwrap();
        const url = window.URL.createObjectURL(new Blob([result]));
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.setAttribute("download", fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success(`Report downloaded successfully.`);
      } catch (error) {
        toast.error(
          error?.data?.message ||
            error?.message ||
            "Failed to download the file. Please try again."
        );
      }
    } else {
      toast.error("No data available for download.");
    }
  };

  return (
    <>
      <div
        className="px-3 px-lg-5 firm-reports-page-container"
        data-testid="usage-reports-page"
      >
        <div className="firm-reports-second-div">
          <div className="d-flex justify-content-center justify-content-space-between gap-4 flex-wrap d-block d-lg-none">
            <Container>
              <Row className="justify-content-center">
                <Col className="p-3" lg={2} md={6} sm={12}>
                  <div
                    className={`pt-2 bg-grey firm-report-tiles-2 clickable-tile`}
                    data-testid="capacity-util"
                  >
                    <div className="px-2 firm-reports-tiles firm-titles-header">
                      {reportPlaceholder.capacityUtilization}
                    </div>
                    <div className="px-2 firm-titles-value">
                    {capacityUtil ? `${capacityUtil} GB` : "0 GB"}
                    </div>
                  </div>
                </Col>
                <Col className="p-3" lg={2} md={6} sm={12}>
                  <div
                    className={`pt-2 bg-blue firm-report-tiles-2 clickable-tile`}
                    data-testid="doc-per-user"
                  >
                    <div className="px-2 firm-reports-tiles firm-titles-header">
                      {reportPlaceholder.documentsPerUser}
                    </div>
                    <div className="px-2 firm-titles-value">
                      {docPerUser || 0}
                    </div>
                  </div>
                </Col>
                <Col className="p-3" lg={2} md={6} sm={12}>
                  <div
                    className={`pt-2 bg-red firm-report-tiles-2 clickable-tile`}
                    data-testid="cap-util-per-user"
                  >
                    <div className="px-2 firm-reports-tiles firm-titles-header">
                      {reportPlaceholder.capacityUtilizationPerUser}
                    </div>
                    <div className="px-2 firm-titles-value">
                      {capacityPerUser ? `${capacityPerUser} GB` : "0 GB"}
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
          <div className="d-none d-lg-block">
            <div className="py-3 px-3 d-flex justify-content-center justify-content-space-between gap-4 flex-wrap">
              <div
                className={`pt-2 bg-grey firm-report-tiles-2`}
                data-testid="capacity-util"
              >
                <div className="px-2 firm-reports-tiles firm-titles-header">
                  {reportPlaceholder.capacityUtilization}
                </div>
                <div className="px-2 firm-titles-value">
                  {capacityUtil ? `${capacityUtil} GB` : "0 GB"}
                </div>
              </div>
              <div
                className={`pt-2 bg-blue firm-report-tiles-2`}
                data-testid="doc-per-user"
              >
                <div className="px-2 firm-reports-tiles firm-titles-header">
                  {reportPlaceholder.documentsPerUser}
                </div>
                <div className="px-2 firm-titles-value">{docPerUser || 0}</div>
              </div>
              <div
                className={`pt-2 bg-red firm-report-tiles-2`}
                data-testid="cap-util-per-user"
              >
                <div className="px-2 firm-reports-tiles firm-titles-header">
                  {reportPlaceholder.capacityUtilizationPerUser}
                </div>
                <div className="px-2 firm-titles-value">
                  {capacityPerUser ? `${capacityPerUser} GB` : "0 GB"}
                </div>
              </div>
            </div>
          </div>

          <div className="px-3 py-2 search-container d-block d-sm-none">
            <div className="search-icon">
              <AGSearchIcon />
            </div>
            <div className="search-input-field ">
              <input
                type="text"
                placeholder="Search"
                value={searchText}
                autoComplete="off"
                onChange={handleSearchChange}
                id="search-firm-input-field"
                data-testid="grid-search-input"
              />
            </div>
            {searchText && (
              <div
                onClick={handleClearSearch}
                className="clear-search-button-mb"
              >
                <SearchXIcon />
              </div>
            )}
          </div>

          <div className="firm-reports-second-div py-2">
            <div className="d-flex px-3 justify-content-between align-items-center">
              <div className="firm-reports-table-title d-flex"></div>
              <div className="d-flex gap-3">
                <div className="d-flex">
                  <div className="search-container d-none d-md-block">
                    <div className="search-icon">
                      <AGSearchIcon />
                    </div>
                    <div className="search-input-field flex-1">
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchText}
                        autoComplete="off"
                        onChange={handleSearchChange}
                        id="search-firm-input-field"
                        data-testid="grid-search-input"
                      />
                    </div>
                    {searchText && (
                      <div
                        onClick={handleClearSearch}
                        className="clear-search-button"
                      >
                        <SearchXIcon />
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <div>
                    <Dropdown>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip className="tooltip">Download</Tooltip>
                        }
                      >
                        <Dropdown.Toggle
                          as="div"
                          className="ms-2 cursor-pointer"
                          data-testid="download"
                        >
                          <Download />
                        </Dropdown.Toggle>
                      </OverlayTrigger>
                      <Dropdown.Menu align="right">
                        <Dropdown.Item onClick={() => handleDownload("PDF")}>
                          {reportPlaceholder.downloadAsPDF}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleDownload("CSV")}>
                          {reportPlaceholder.downloadAsCSV}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip className="tooltip">Share</Tooltip>}
                    >
                      <div className="ms-3 cursor-pointer" data-testid="share">
                        <Share onClick={handleShare} />
                      </div>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="py-2 px-3">
            <AgGrid
              quickFilterText={searchText}
              rowData={customFilter}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowStyle={getRowStyle}
              paginationSizeLimit={paginationSizeLimit}
              rowSelection={"multiple"}
            />
            {(isLoading || download) && <Loader data-testid="loader" />}
          </div>
        </div>
      </div>
      <ShareReportModalPopup
        showModal={showModal}
        setShowCallback={() => setShowModal(false)}
        bodyData={[...headerRow,...filteredFields]}
        shareFileName={fileName}
      />
    </>
  );
}
